import { Input } from '@/components/ui/input';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { useMemo } from 'react';

export type EventsFilterBarProps = {
  createdFrom: string;
  onCreatedFromChange: (value: string) => void;
  createdTo: string;
  onCreatedToChange: (value: string) => void;
  selectedAorIds: string[];
  onAorIdsChange: (ids: string[]) => void;
};

const PICKER_INPUT_CLASS =
  'tw:h-8 tw:min-w-0 tw:flex-1 tw:text-xs tw:scheme-dark tw:[&::-webkit-calendar-picker-indicator]:cursor-pointer tw:[&::-webkit-calendar-picker-indicator]:opacity-100 tw:[&::-webkit-calendar-picker-indicator]:invert';

function datePart(value: string): string {
  return value.slice(0, 10);
}

function timePart(value: string): string {
  return value.length >= 16 ? value.slice(11, 16) : '';
}

function joinDateTime(
  date: string,
  time: string,
  fallbackTime: string
): string {
  if (!date) return '';
  return `${date}T${time || fallbackTime}`;
}

export function EventsFilterBar({
  createdFrom,
  onCreatedFromChange,
  createdTo,
  onCreatedToChange,
  selectedAorIds,
  onAorIdsChange,
}: EventsFilterBarProps) {
  const { data: aors = [] } = useAors();
  const aorText = useAorText();
  const aorOptions = useMemo(
    () =>
      aors.map((aor) => ({
        value: aor.id,
        label: aor.name,
      })),
    [aors]
  );

  return (
    <div className="tw:space-y-2">
      <div className="tw:space-y-1">
        <span className="tw:text-xs tw:text-muted-foreground">From</span>
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <Input
            type="date"
            value={datePart(createdFrom)}
            onChange={(event) =>
              onCreatedFromChange(
                joinDateTime(event.target.value, timePart(createdFrom), '00:00')
              )
            }
            className={PICKER_INPUT_CLASS}
            aria-label="Created from date"
          />
          <Input
            type="time"
            value={timePart(createdFrom)}
            onChange={(event) => {
              if (!datePart(createdFrom)) return;
              onCreatedFromChange(
                joinDateTime(datePart(createdFrom), event.target.value, '00:00')
              );
            }}
            className={PICKER_INPUT_CLASS}
            aria-label="Created from time"
          />
        </div>
      </div>
      <div className="tw:space-y-1">
        <span className="tw:text-xs tw:text-muted-foreground">To</span>
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <Input
            type="date"
            value={datePart(createdTo)}
            onChange={(event) =>
              onCreatedToChange(
                joinDateTime(event.target.value, timePart(createdTo), '23:59')
              )
            }
            className={PICKER_INPUT_CLASS}
            aria-label="Created to date"
          />
          <Input
            type="time"
            value={timePart(createdTo)}
            onChange={(event) => {
              if (!datePart(createdTo)) return;
              onCreatedToChange(
                joinDateTime(datePart(createdTo), event.target.value, '23:59')
              );
            }}
            className={PICKER_INPUT_CLASS}
            aria-label="Created to time"
          />
        </div>
      </div>
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
        <span className="tw:text-xs tw:text-muted-foreground">
          {aorText('AOR')}
        </span>
        <MultiIdCombobox
          options={aorOptions}
          value={selectedAorIds}
          onChange={onAorIdsChange}
          placeholder={aorText('All AORs')}
        />
      </div>
    </div>
  );
}
